import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const EVENT_TYPES = [
  { v: "wedding", l: "Wedding" },
  { v: "gala", l: "Gala / Corporate" },
  { v: "birthday", l: "Birthday" },
  { v: "engagement", l: "Engagement" },
  { v: "brand", l: "Brand activation" },
  { v: "other", l: "Something else" },
];
const PACKAGES = [
  { v: "essentials", l: "Essentials — $450 · 2hrs" },
  { v: "professional", l: "Professional — $650 · 3hrs" },
  { v: "enterprise", l: "Enterprise — $850 · 4hrs" },
  { v: "custom", l: "I'm not sure yet" },
];

const reveal = {
  initial: { y: 40, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "wedding",
    package: "professional",
    guest_count: "",
    location: "",
    message: "",
  });
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    try {
      const p = window.localStorage.getItem("pb_selected_package");
      if (p) setForm((f) => ({ ...f, package: p }));
    } catch (_e) {
      // ignore storage errors
    }
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : 0,
        event_date: date ? format(date, "yyyy-MM-dd") : "",
      });
      toast.success("We received your inquiry. Expect a reply within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        event_type: "wedding",
        package: "professional",
        guest_count: "",
        location: "",
        message: "",
      });
      setDate(undefined);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative bg-[#FDFBF7] py-24 md:py-40" data-testid="booking-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div {...reveal} className="lg:col-span-5">
          <div className="overline text-[#0A0A0A]/60 mb-4">Reserve</div>
          <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#0A0A0A]">
            Tell us about <em className="text-[#FF5722]">the night</em>.
          </h2>
          <p className="mt-6 text-[#0A0A0A]/75 max-w-md leading-relaxed">
            Share a few details and we&apos;ll come back with a proposal, sample gallery, and available dates within one
            business day.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div className="flex items-center gap-4">
              <span className="overline w-24">Studio</span>
              <span>Chicagoland, IL · Serving the greater Chicago area</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="overline w-24">Email</span>
              <a href="mailto:photoboothmemories2024@gmail.com" className="hover:text-[#FF5722]" data-testid="contact-email">
                photoboothmemories2024@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="overline w-24">George</span>
              <a href="tel:+17089403182" className="hover:text-[#FF5722]" data-testid="contact-phone-george">
                (708) 940 · 3182
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="overline w-24">Lucy</span>
              <a href="tel:+17087699419" className="hover:text-[#FF5722]" data-testid="contact-phone-lucy">
                (708) 769 · 9419
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          {...reveal}
          onSubmit={onSubmit}
          className="lg:col-span-7 border border-[#0A0A0A]/10 bg-white p-8 md:p-12 rounded-sm"
          data-testid="booking-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Your name" required>
              <Input value={form.name} onChange={set("name")} placeholder="Alex Morgan" data-testid="input-name" />
            </Field>
            <Field label="Email" required>
              <Input value={form.email} onChange={set("email")} type="email" placeholder="you@domain.com" data-testid="input-email" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" data-testid="input-phone" />
            </Field>
            <Field label="Guest count">
              <Input value={form.guest_count} onChange={set("guest_count")} type="number" min="0" placeholder="120" data-testid="input-guests" />
            </Field>

            <Field label="Event date">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="date-picker-trigger"
                    className="w-full flex items-center justify-between border border-[#0A0A0A]/15 rounded-sm px-3 py-2 text-sm bg-white hover:border-[#FF5722] transition-colors"
                  >
                    <span className={date ? "text-[#0A0A0A]" : "text-[#0A0A0A]/50"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-[#0A0A0A]/60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus data-testid="date-picker-calendar" />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Event type">
              <Select value={form.event_type} onValueChange={set("event_type")}>
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue placeholder="Choose an event" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Package" full>
              <Select value={form.package} onValueChange={set("package")}>
                <SelectTrigger data-testid="package-select-trigger">
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Venue / City" full>
              <Input value={form.location} onChange={set("location")} placeholder="Venue name or city" data-testid="input-location" />
            </Field>

            <Field label="Tell us about your event" full>
              <Textarea
                value={form.message}
                onChange={set("message")}
                placeholder="Vibe, timing, any custom requests…"
                rows={4}
                data-testid="input-message"
              />
            </Field>
          </div>

          <div className="mt-10 flex items-center justify-between">
            <span className="text-xs text-[#0A0A0A]/60">We reply within 24 business hours.</span>
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-[#0A0A0A] text-[#FDFBF7] hover:bg-[#FF5722] transition-colors disabled:opacity-60"
              data-testid="booking-form-submit-button"
            >
              <span className="text-sm tracking-wide">{submitting ? "Sending…" : "Send inquiry"}</span>
              <span className="w-9 h-9 rounded-full bg-[#FDFBF7] text-[#0A0A0A] flex items-center justify-center group-hover:rotate-45 transition-transform">
                →
              </span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="overline text-[#0A0A0A]/70 mb-2 block">
        {label} {required && <span className="text-[#FF5722]">*</span>}
      </Label>
      {children}
    </div>
  );
}
